import React from "react";
import { observer } from "mobx-react-lite";
import { Button, SvgIcon, Typography, UseBooleanReturnType } from "@gemlightbox/core-kit";

import { useStores } from "src/hooks";
import { MediaType } from "src/models";
import { maxImagesPerBatch } from "../../media.constants";

import { ReactComponent as AIRetouch } from "src/external-ts/assets/images/edit-media/ai-retouch-icon.svg";

type AIRetouchButtonProps = {
  visible: UseBooleanReturnType;
  contentSet: React.Dispatch<React.SetStateAction<string>>;
};

export const AIRetouchButton: React.FC<AIRetouchButtonProps> = observer(
  ({ visible, contentSet }) => {
    const { mediaStore, modalsStore, tooltipStore, localeStore, subscriptionsStore, userStore } =
      useStores();

    const isMoreThanMaxImagesPerBatch =
      mediaStore.selectedMediaAmount > maxImagesPerBatch ||
      mediaStore.isPendingUploedMediaSelected ||
      mediaStore.isEclipseModeMediaSelected ||
      mediaStore.isAiRetouchMediaSelected;

    const hasOnlyImages = mediaStore.selectedMediaList.every(
      (media) => media.type === MediaType.image,
    );

    const isDisabled = !hasOnlyImages || isMoreThanMaxImagesPerBatch;

    const handlePointerEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!hasOnlyImages) {
        tooltipStore.open(
          <Typography size="small">
            {localeStore.t(
              'media["media-selection-panel"]["ai-retouch-button"]["only-images-tooltip"].title',
            )}
          </Typography>,
          {
            position: "bottom",
            appearance: "secondary",
            target: e.currentTarget,
            withAngle: true,
          },
        );

        return;
      }
    };

    const handlePointerLeave = () => {
      if (hasOnlyImages) return;
      tooltipStore.close();
    };

    const handleRemoveBackground = () => {
      if (
        !userStore.isSubscribed &&
        (userStore.userUsage?.countRetouch ?? 0) >= subscriptionsStore.limits.availableRetouch
      ) {
        contentSet(localeStore.t('common["pro-user"]["ai-retouch-upgrade-text"]'));
        visible.setTruthy();
        return;
      }

      modalsStore.open("BatchAIRetouchModal", {
        medias: mediaStore.selectedMediaList,
      });
      mediaStore.unselectAllMediaList("selected");
    };

    return (
      <Button
        appearance="primaryGhost"
        onClick={handleRemoveBackground}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        disabled={isDisabled}
        data-cy="ai-retouch"
      >
        <SvgIcon icon={AIRetouch} />
        AI {localeStore.t('media["media-selection-panel"]["ai-retouch-button"].buttons.retouch')}
      </Button>
    );
  },
);

export default AIRetouchButton;
